import React from 'react';
import { Typography as MuiTypography } from '@mui/material';
import PropTypes from 'prop-types';
import { tokens } from '../../styles/theme';

/**
 * Typography Atom
 * A base typography component that applies our design system text styles
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Text content
 * @param {string} props.variant - MUI typography variant (h1, h2, body1, etc.)
 * @param {string} props.component - HTML element to render (h1, h2, p, etc.)
 * @param {string} props.color - Text color from the theme
 * @param {string} props.align - Text alignment
 * @param {number|string} props.fontSize - Custom font size
 * @param {number|string} props.fontWeight - Custom font weight
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Typography component
 */
const Typography = ({
  children,
  variant = 'body1',
  component,
  color = 'text.primary',
  align,
  fontSize,
  fontWeight,
  sx = {},
  ...rest
}) => {
  // Build sx styles with props
  const styles = {
    ...(fontSize && { fontSize }),
    ...(fontWeight && { fontWeight }),
    ...sx
  };

  return (
    <MuiTypography
      variant={variant}
      component={component}
      color={color}
      align={align}
      sx={styles}
      {...rest}
    >
      {children}
    </MuiTypography>
  );
};

Typography.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'button',
    'caption',
    'overline'
  ]),
  component: PropTypes.string,
  color: PropTypes.string,
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object
};

export default Typography;